import * as THREE from 'three';
import * as WebIFC from 'web-ifc';

/**
 * Basic IFC preview renderer (Phase 2, Beta).
 * Loads an IFC model with web-ifc, builds three.js meshes, and captures a
 * top-down (plan) orthographic snapshot as a raster image. The snapshot flows
 * into the same viewer as PDF/DXF so jobs can be pinned onto it.
 *
 * This is a STATIC snapshot + pin approach (not a live-navigable 3D model).
 */
export async function renderIfcToImage(arrayBuffer, maxSize = 1600) {
  const ifcApi = new WebIFC.IfcAPI();
  // Anchor the wasm to the site origin and mark it absolute, otherwise web-ifc
  // resolves it relative to the current route (e.g. /app/projects/.. ) and the
  // SPA fallback returns index.html instead of the .wasm binary.
  ifcApi.SetWasmPath(`${window.location.origin}/`, true);
  await ifcApi.Init();

  let modelID = -1;
  const group = new THREE.Group();
  try {
    modelID = ifcApi.OpenModel(new Uint8Array(arrayBuffer), { COORDINATE_TO_ORIGIN: true });

    ifcApi.StreamAllMeshes(modelID, (mesh) => {
      const geoms = mesh.geometries;
      for (let i = 0; i < geoms.size(); i++) {
        const pg = geoms.get(i);
        const g = ifcApi.GetGeometry(modelID, pg.geometryExpressID);
        const verts = ifcApi.GetVertexArray(g.GetVertexData(), g.GetVertexDataSize());
        const indices = ifcApi.GetIndexArray(g.GetIndexData(), g.GetIndexDataSize());

        const positions = new Float32Array(verts.length / 2);
        for (let v = 0; v < verts.length; v += 6) {
          positions[(v / 6) * 3] = verts[v];
          positions[(v / 6) * 3 + 1] = verts[v + 1];
          positions[(v / 6) * 3 + 2] = verts[v + 2];
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geometry.setIndex(new THREE.BufferAttribute(new Uint32Array(indices), 1));
        geometry.computeVertexNormals();

        const c = pg.color || { x: 0.6, y: 0.6, z: 0.62, w: 1 };
        const mat = new THREE.MeshLambertMaterial({
          color: new THREE.Color(c.x, c.y, c.z),
          side: THREE.DoubleSide,
          transparent: c.w < 1,
          opacity: c.w != null ? c.w : 1,
        });
        const meshObj = new THREE.Mesh(geometry, mat);
        meshObj.applyMatrix4(new THREE.Matrix4().fromArray(pg.flatTransformation));
        group.add(meshObj);
        g.delete();
      }
    });
  } finally {
    if (modelID !== -1) {
      try { ifcApi.CloseModel(modelID); } catch (e) { /* ignore */ }
    }
  }

  if (group.children.length === 0) {
    throw new Error('IFC model contains no renderable geometry');
  }

  const box = new THREE.Box3().setFromObject(group);
  const size = new THREE.Vector3();
  const center = new THREE.Vector3();
  box.getSize(size);
  box.getCenter(center);

  const worldW = size.x || 1;
  const worldH = size.y || 1;
  const aspect = worldH / worldW;
  let width = maxSize;
  let height = Math.round(maxSize * aspect);
  if (height > maxSize) {
    height = maxSize;
    width = Math.round(maxSize / aspect);
  }
  width = Math.max(300, width);
  height = Math.max(300, height);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0xffffff);
  scene.add(group);
  scene.add(new THREE.AmbientLight(0xffffff, 0.85));
  const dir = new THREE.DirectionalLight(0xffffff, 0.55);
  dir.position.set(0.5, 0.6, 1);
  scene.add(dir);

  const margin = 1.06;
  const camera = new THREE.OrthographicCamera(
    (-worldW / 2) * margin, (worldW / 2) * margin,
    (worldH / 2) * margin, (-worldH / 2) * margin,
    0.01, (Math.max(worldW, worldH, size.z) + size.z) * 4
  );
  camera.position.set(center.x, center.y, box.max.z + Math.max(worldW, worldH, size.z) + 1);
  camera.up.set(0, 1, 0);
  camera.lookAt(center.x, center.y, center.z);

  const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
  renderer.setSize(width, height);
  renderer.setClearColor(0xffffff, 1);
  renderer.render(scene, camera);
  const dataUrl = renderer.domElement.toDataURL('image/png');

  // cleanup
  group.traverse((obj) => {
    if (obj.geometry) obj.geometry.dispose();
    if (obj.material) obj.material.dispose();
  });
  renderer.dispose();

  return { dataUrl, width, height };
}
